import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { forwardRef, useRef, useEffect } from "react";
import { FieldOverlay } from "../FieldOverlay/FieldOverlay.mjs";
import { Box } from "../../Box/Box.mjs";
import "../../Text/TextContext.mjs";
import "../../Heading/HeadingContext.mjs";
import "../../../css/lineHeightContainer.css.mjs";
import { buildDataAttributes } from "../buildDataAttributes.mjs";
import "../../../css/reset/reset.css.mjs";
import "../../../css/atoms/sprinkles.css.mjs";
import "../../../hooks/useIcon/icon.css.mjs";
import "../../../css/typography.css.mjs";
import { IconDone } from "../../icons/IconDone/IconDone.mjs";
import { IconRemove } from "../../icons/IconRemove/IconRemove.mjs";
import { realField, fullField, realFieldPosition, isMixed, fakeField, fakeFieldSize, selected, focusOverlay, hoverOverlay, checkboxIndicator, radioIndicator, mixedBorderSmall, mixedBorder } from "./InlineField.css.mjs";
const tones = ["neutral", "critical"];
const Indicator = ({
  type,
  checked,
  hover = false,
  disabled = false
}) => {
  const isCheckbox = type === "checkbox";
  const iconTone = (() => {
    if (disabled) {
      return "disabled";
    }
    if (hover) {
      return "formAccent";
    }
  })();
  return isCheckbox ? /* @__PURE__ */ jsxs(
    Box,
    {
      height: "full",
      transition: "fast",
      position: "relative",
      className: checkboxIndicator,
      children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            position: "absolute",
            inset: 0,
            transition: "fast",
            opacity: checked !== "mixed" ? 0 : void 0,
            children: /* @__PURE__ */ jsx(IconRemove, { size: "fill", tone: iconTone })
          }
        ),
        /* @__PURE__ */ jsx(
          Box,
          {
            position: "absolute",
            inset: 0,
            transition: "fast",
            opacity: checked !== true ? 0 : void 0,
            children: /* @__PURE__ */ jsx(IconDone, { size: "fill", tone: iconTone })
          }
        )
      ]
    }
  ) : /* @__PURE__ */ jsx(
    Box,
    {
      background: disabled ? { lightMode: "neutralLight", darkMode: "surfaceDark" } : "formAccent",
      transition: "fast",
      width: "full",
      height: "full",
      borderRadius: "full",
      className: radioIndicator
    }
  );
};
const StyledInput = forwardRef(
  ({
    id,
    name,
    value,
    checked,
    data,
    onChange,
    type,
    tone = "neutral",
    disabled = false,
    required,
    tabIndex,
    size = "standard",
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    "aria-label": ariaLabel,
    fullField: fullField$1,
    ...restProps
  }, forwardedRef) => {
    const defaultRef = useRef(null);
    const ref = forwardedRef || defaultRef;
    const indeterminateRef = useRef(false);
    if (tones.indexOf(tone) === -1) {
      throw new Error(`Invalid tone: ${tone}`);
    }
    const isCheckbox = type === "checkbox";
    const fieldBorderRadius = isCheckbox ? "xsmall" : "full";
    const accentBackground = disabled ? void 0 : "formAccent";
    const isMixed$1 = isCheckbox && checked === "mixed";
    const borderMapObj = {
      small: mixedBorderSmall,
      standard: mixedBorder
    };
    const mixedBorderStyling = isMixed$1 ? borderMapObj[size] : void 0;
    const fieldBackground = disabled ? { lightMode: "disabled", darkMode: "neutral" } : { lightMode: "surface" };
    useEffect(() => {
      if (ref && typeof ref === "object" && ref.current && isCheckbox) {
        ref.current.indeterminate = isMixed$1;
        indeterminateRef.current = isMixed$1;
      }
    }, [ref, isMixed$1, isCheckbox]);
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        Box,
        {
          component: "input",
          type,
          id,
          name,
          value,
          onChange: isMixed$1 ? (e) => {
            if (ref && typeof ref === "object" && ref.current) {
              ref.current.indeterminate = indeterminateRef.current;
            }
            if (typeof onChange === "function") {
              onChange(e);
            }
          } : onChange,
          checked: checked === "mixed" ? false : checked,
          position: "absolute",
          zIndex: 1,
          className: [
            realField,
            fullField$1 ? fullField : void 0,
            realFieldPosition[size],
            isMixed$1 ? isMixed : void 0
          ],
          cursor: !disabled ? "pointer" : void 0,
          opacity: 0,
          "aria-describedby": ariaDescribedBy,
          "aria-labelledby": ariaLabelledBy,
          "aria-label": ariaLabel,
          "aria-checked": isMixed$1 ? "mixed" : checked,
          "aria-required": required,
          disabled,
          ref,
          tabIndex,
          ...buildDataAttributes({ data, validateRestProps: restProps })
        }
      ),
      /* @__PURE__ */ jsxs(
        Box,
        {
          flexShrink: 0,
          position: "relative",
          className: [fakeField, fakeFieldSize[size]],
          background: fieldBackground,
          borderRadius: fieldBorderRadius,
          children: [
            /* @__PURE__ */ jsx(
              FieldOverlay,
              {
                variant: disabled ? "disabled" : "default",
                borderRadius: fieldBorderRadius,
                visible: tone !== "critical" || disabled
              }
            ),
            /* @__PURE__ */ jsx(
              FieldOverlay,
              {
                variant: "critical",
                borderRadius: fieldBorderRadius,
                visible: tone === "critical" && !disabled
              }
            ),
            /* @__PURE__ */ jsx(
              FieldOverlay,
              {
                variant: tone === "critical" && isCheckbox ? tone : void 0,
                background: isCheckbox ? accentBackground : void 0,
                borderRadius: isMixed$1 ? void 0 : fieldBorderRadius,
                className: [selected, mixedBorderStyling],
                children: /* @__PURE__ */ jsx(Indicator, { type, disabled, checked })
              }
            ),
            /* @__PURE__ */ jsx(
              FieldOverlay,
              {
                variant: "focus",
                borderRadius: fieldBorderRadius,
                className: focusOverlay
              }
            ),
            /* @__PURE__ */ jsx(
              FieldOverlay,
              {
                variant: "hover",
                borderRadius: fieldBorderRadius,
                className: hoverOverlay
              }
            )
          ]
        }
      )
    ] });
  }
);
StyledInput.displayName = "StyledInput";
export {
  StyledInput
};
